import { useEffect, useMemo, useState } from "react";
import "./DateRangeModal.css";

const WEEKDAYS = ["dom", "seg", "ter", "qua", "qui", "sex", "sab"];
const MONTHS = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

function toLocalDate(dateStr) {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function formatDateKey(d) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDisplay(d) {
  if (!d) return "Selecione";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
}

function isSameDay(a, b) {
  return !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function buildMonthGrid(year, month) {
  const firstOfMonth = new Date(year, month, 1);
  const startWeekday = firstOfMonth.getDay();
  const gridStart = new Date(year, month, 1 - startWeekday);

  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
    days.push(d);
  }
  return days;
}

export default function DateRangeModal({ open, initialStart, initialEnd, onApply, onClose }) {
  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [hoverDate, setHoverDate] = useState(null);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  useEffect(() => {
    if (!open) return;
    const start = toLocalDate(initialStart) || today;
    const end = toLocalDate(initialEnd) || start;
    setRangeStart(start);
    setRangeEnd(end);
    setViewYear(start.getFullYear());
    setViewMonth(start.getMonth());
    setHoverDate(null);
  }, [open, initialStart, initialEnd, today]);

  if (!open) return null;

  const yearOptions = [];
  for (let y = today.getFullYear(); y >= today.getFullYear() - 4; y--) {
    yearOptions.push(y);
  }

  const days = buildMonthGrid(viewYear, viewMonth);

  const previewEnd = rangeEnd || hoverDate;
  const hasRange = !!(rangeStart && previewEnd && !isSameDay(rangeStart, previewEnd));

  function handleDayClick(day) {
    if (day > today) return;

    if (!rangeStart || (rangeStart && rangeEnd)) {
      setRangeStart(day);
      setRangeEnd(null);
      return;
    }

    if (day < rangeStart) {
      setRangeEnd(rangeStart);
      setRangeStart(day);
    } else {
      setRangeEnd(day);
    }
  }

  function goToMonth(delta) {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setViewMonth(m);
    setViewYear(y);
  }

  function handleApply() {
    if (!rangeStart) return;
    const end = rangeEnd || rangeStart;
    onApply({ startDate: formatDateKey(rangeStart), endDate: formatDateKey(end) });
  }

  function selectQuickRange(days) {
    const end = today;
    const start = new Date(today);
    start.setDate(today.getDate() - (days - 1));
    setRangeStart(start);
    setRangeEnd(end);
    setViewYear(start.getFullYear());
    setViewMonth(start.getMonth());
  }

  return (
    <div className="date-modal-overlay" onClick={onClose}>
      <div className="date-modal" onClick={(e) => e.stopPropagation()}>
        <div className="date-modal-header">
          <h3>Período personalizado</h3>
          <button type="button" className="date-modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="date-modal-display">
          <div className={`date-modal-display-item ${!rangeEnd ? "active" : ""}`}>
            <span className="date-modal-display-label">De</span>
            <span className="date-modal-display-value">{formatDisplay(rangeStart)}</span>
          </div>
          <i className="fas fa-arrow-right date-modal-arrow" />
          <div className={`date-modal-display-item ${rangeStart && !rangeEnd ? "active" : ""}`}>
            <span className="date-modal-display-label">Até</span>
            <span className="date-modal-display-value">{formatDisplay(rangeEnd)}</span>
          </div>
        </div>

        <div className="date-modal-quick">
          <button type="button" onClick={() => selectQuickRange(1)}>Hoje</button>
          <button type="button" onClick={() => selectQuickRange(7)}>7 dias</button>
          <button type="button" onClick={() => selectQuickRange(15)}>15 dias</button>
          <button type="button" onClick={() => selectQuickRange(30)}>30 dias</button>
        </div>

        <div className="date-modal-month-nav">
          <button type="button" className="date-modal-nav-btn" onClick={() => goToMonth(-1)}>
            <i className="fas fa-chevron-left" />
          </button>
          <div className="date-modal-pickers">
            <select value={viewMonth} onChange={(e) => setViewMonth(Number(e.target.value))}>
              {MONTHS.map((m, idx) => (
                <option key={m} value={idx}>{m}</option>
              ))}
            </select>
            <select value={viewYear} onChange={(e) => setViewYear(Number(e.target.value))}>
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="date-modal-nav-btn"
            onClick={() => goToMonth(1)}
            disabled={viewYear === today.getFullYear() && viewMonth === today.getMonth()}
          >
            <i className="fas fa-chevron-right" />
          </button>
        </div>

        <div className="date-modal-weekdays">
          {WEEKDAYS.map((w) => (
            <span key={w}>{w}</span>
          ))}
        </div>

        <div className="date-modal-days" onMouseLeave={() => setHoverDate(null)}>
          {days.map((day, idx) => {
            const isPassive = day.getMonth() !== viewMonth;
            const isDisabled = day > today;
            const isStart = isSameDay(day, rangeStart);
            const inRange = rangeStart && previewEnd && day > (rangeStart < previewEnd ? rangeStart : previewEnd) && day < (rangeStart < previewEnd ? previewEnd : rangeStart);
            const isToday = isSameDay(day, today);

            return (
              <button
                type="button"
                key={idx}
                disabled={isDisabled}
                className={[
                  "date-modal-day",
                  isPassive ? "passive" : "",
                  isDisabled ? "disabled" : "",
                  isStart ? "range-start" : "",
                  (rangeEnd && isSameDay(day, rangeEnd)) ? "range-end" : "",
                  inRange ? "in-range" : "",
                  hasRange && (isStart || (rangeEnd && isSameDay(day, rangeEnd)) || (!rangeEnd && isSameDay(day, hoverDate))) ? "edge" : "",
                  isToday ? "today" : "",
                ].filter(Boolean).join(" ")}
                onClick={() => handleDayClick(day)}
                onMouseEnter={() => !rangeEnd && setHoverDate(day)}
              >
                <span>{day.getDate()}</span>
              </button>
            );
          })}
        </div>

        <div className="date-modal-footer">
          <button type="button" className="date-modal-btn-cancel" onClick={onClose}>Cancelar</button>
          <button type="button" className="date-modal-btn-apply" onClick={handleApply} disabled={!rangeStart}>
            Aplicar
          </button>
        </div>
      </div>
    </div>
  );
}
